import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

const MB = 1024 * 1024;

// Sub-megabyte files read better in KB; anything larger gets one decimal so a 8.04MB file is not
// rounded to "8MB" against an 8MB limit.
const formatSize = (bytes) =>
  bytes < MB
    ? `${Math.round(bytes / 1024)}KB`
    : `${(bytes / MB).toFixed(1)}MB`;

const listNames = (rejected) => {
  const names = rejected.map((file) => file.name);
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

const FileDropZone = ({
  onFilesChange,
  files,
  onRemove,
  maxFiles = 3,
  // Must not exceed spring.servlet.multipart.max-file-size in authentication-service, which is
  // itself bounded by the API Gateway 10MB per-request cap. Anything larger is accepted here and
  // then rejected mid-upload with MaxUploadSizeExceededException.
  maxSize = 8,
}) => {
  // react-dropzone hands rejected files to onDrop separately, and anything past maxFiles is
  // trimmed here. Both used to be discarded silently, so an oversized or fourth document simply
  // never appeared and the client had no idea why.
  const [rejections, setRejections] = useState([]);

  const describeRejection = useCallback(
    ({ file, errors }) => {
      if (errors?.some((error) => error.code === 'file-too-large')) {
        // Quote the file's real size so the client knows how much to compress by, rather than
        // having to guess against a limit they cannot see.
        return `${file.name} is ${formatSize(file.size)} — the limit is ${maxSize}MB per file`;
      }
      if (errors?.some((error) => error.code === 'file-invalid-type')) {
        return `${file.name} is not an accepted format — use PDF, JPG, PNG or DOC`;
      }
      return `${file.name} could not be added`;
    },
    [maxSize]
  );

  const onDrop = useCallback(
    (acceptedFiles, fileRejections = []) => {
      const messages = fileRejections.map(describeRejection);

      const newFiles = [...files, ...acceptedFiles];
      // limit to maxFiles
      const validFiles = newFiles.slice(0, maxFiles);

      // Name the documents that were dropped - "1 more was not added" leaves the client checking
      // the list to work out which one went missing.
      const discarded = newFiles.slice(maxFiles);
      if (discarded.length > 0) {
        messages.push(
          `Only ${maxFiles} documents can be attached — ${listNames(
            discarded
          )} ${discarded.length === 1 ? 'was' : 'were'} not added. Remove one to attach ${
            discarded.length === 1 ? 'it' : 'another'
          }.`
        );
      }

      setRejections(messages);
      onFilesChange(validFiles);
    },
    [onFilesChange, files, maxFiles, describeRejection]
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.png', '.jpg'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
    },
    multiple: true,
    maxSize: maxSize * 1024 * 1024, // MB to bytes
  });

  return (
    <>
    <div className="d-flex" style={{ minHeight: '120px', gap: '10px' }}>
      {/* Drop Zone */}
      <div
        {...getRootProps()}
        className={`flex-grow-1 p-3 rounded-3 border-2 ${isDragActive ? 'border-primary bg-light' : 'border-dashed'}`}
        style={{
          backgroundColor: isDragActive ? 'rgba(0, 116, 200, 0.05)' : '#f8f9fa',
          borderColor: isDragActive ? '#0074C8' : '#ced4da',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          flexBasis: files.length > 0 ? '50%' : '100%',
        }}
      >
        <input {...getInputProps()} />
        <div className="h-100 d-flex flex-column justify-content-center text-center">
          <i
            className="bi bi-cloud-arrow-up mb-2"
            style={{ fontSize: '2rem', color: '#0074C8' }}
          ></i>
          {isDragActive ? (
            <p className="fw-bold mb-0" style={{ color: '#0074C8' }}>
              Drop files here
            </p>
          ) : (
            <>
              <p className="mb-1" style={{ color: '#0074C8' }}>
                Drag & drop files here
              </p>
              <small className="text-muted">
                or click to browse (PDF, JPG, PNG, DOC)
              </small>
              <small className="text-muted">
                up to {maxFiles} files, max {maxSize}MB each
              </small>
            </>
          )}
        </div>
      </div>

      {/* File Preview */}
      {files.length > 0 && (
        <div
          className="flex-grow-1 p-3 border rounded-3 overflow-auto"
          style={{ flexBasis: '50%', maxHeight: '200px' }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0" style={{ color: '#005a99' }}>
              Selected Files
            </h6>
            <span
              className="badge rounded-pill"
              style={{ backgroundColor: '#0074C8', color: '#fff' }}
            >
              {files.length} file{files.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="list-group">
            {files.map((file, index) => (
              <div
                key={file.name}
                className="list-group-item d-flex justify-content-between align-items-center py-2 px-2 mb-1"
              >
                <div className="d-flex align-items-center text-truncate">
                  <i
                    className="bi bi-file-earmark me-2"
                    style={{ color: '#0074C8' }}
                  ></i>
                  <span className="text-truncate" style={{ maxWidth: '150px' }}>
                    {file.name}
                  </span>
                </div>
                <div
                  className="d-flex align-items-center"
                  style={{ gap: '10px' }}
                >
                  <small className="text-muted me-2">
                    {Math.round(file.size / 1024)} KB
                  </small>
                  <button
                    type="button"
                    className="btn btn-xs btn-outline-danger"
                    onClick={() => onRemove(index)}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    {rejections.length > 0 && (
      <ul className="list-unstyled text-danger small mt-2 mb-0" role="alert">
        {rejections.map((message) => (
          <li key={message}>{message}</li>
        ))}
      </ul>
    )}
    </>
  );
};

export default FileDropZone;
